import { useEffect, useRef, useState } from 'react';
import useStakeholderNotesFunctions from 'components/Dashboard/StakeHolders/StakeholderPage/useStakeholderNotesFunctions';
import useCrmInteractionsQuery from 'components/Dashboard/Influence/CrmInteractions/useCrmInteractionsQuery';

const MIN_REVEAL_DELAY_MS = 1500;
const useStakeholderBioTabCounts = ({ StakeholderInformation, enabled }) => {
  const { objectId, id, groups, contactInformation } = StakeholderInformation ?? {};
  const crmContactId = contactInformation?.[0]?.id ?? id;

  const notesFunctions = useStakeholderNotesFunctions();
  const { userNotes, getNotes } = notesFunctions;

  const initialCall = useRef(null);
  initialCall.current = (source) => {
    if (!enabled) return;
    getNotes({ objectId, requestSource: source, contentType: 'Stakeholder' });
  };

  useEffect(() => {
    const source = new AbortController();
    initialCall.current(source);
    return () => {
      source.abort('Api call cancelled by the user');
    };
  }, [enabled]);

  const {
    interactions,
    isLoading: isLoadingInteractions,
    refetch: refetchInteractionsCount,
  } = useCrmInteractionsQuery({
    filters: [{ field: 'crmContactIds', operator: 'str_eq', value: String(crmContactId) }],
    sort: { field: 'createdAtUtc', order: 'DESC' },
    dependencyKey: `bio-tab-count-${crmContactId}`,
    enabled: enabled && !!crmContactId,
  });

  const dataLoaded = !enabled || (!notesFunctions.isLoading && !isLoadingInteractions);
  const [minDelayElapsed, setMinDelayElapsed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMinDelayElapsed(true), MIN_REVEAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const [countsReady, setCountsReady] = useState(false);
  useEffect(() => {
    if (!countsReady && dataLoaded && minDelayElapsed) {
      setCountsReady(true);
    }
  }, [countsReady, dataLoaded, minDelayElapsed]);

  return {
    notesFunctions,
    notesCount: userNotes?.length ?? 0,
    interactionsCount: interactions?.length ?? 0,
    groupsCount: groups?.length ?? 0,
    countsReady,
    refetchInteractionsCount,
  };
};

export default useStakeholderBioTabCounts;
